/**
 * Platform Detection Service
 *
 * Provides utilities for detecting the current platform (web, iOS, Android)
 * and conditionally executing platform-specific code.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if running as a native app (iOS or Android)
 * @returns {boolean}
 */
export const isNative = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on iOS
 * @returns {boolean}
 */
export const isIOS = () => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running on Android
 * @returns {boolean}
 */
export const isAndroid = () => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on web (browser)
 * @returns {boolean}
 */
export const isWeb = () => {
  return Capacitor.getPlatform() === 'web';
};

/**
 * Check if running on an Apple platform (iOS native, iOS/iPadOS browser, or
 * macOS browser). Used to gate features that only make sense on Apple devices,
 * such as the Sign in with Apple button.
 * @returns {boolean}
 */
export const isApplePlatform = () => {
  if (isIOS()) return true;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac with touch points.
  if (/Macintosh/.test(ua)) return true;
  return false;
};

/**
 * Get the current platform name
 * @returns {'ios' | 'android' | 'web'}
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Check if a specific Capacitor plugin is available
 * @param {string} pluginName - The name of the plugin to check
 * @returns {boolean}
 */
export const isPluginAvailable = (pluginName) => {
  return Capacitor.isPluginAvailable(pluginName);
};

/**
 * Execute platform-specific code
 * @param {Object} handlers - Object with platform-specific handlers
 * @param {Function} [handlers.ios] - Handler for iOS
 * @param {Function} [handlers.android] - Handler for Android
 * @param {Function} [handlers.web] - Handler for web
 * @param {Function} [handlers.native] - Handler for any native platform (iOS or Android)
 * @param {Function} [handlers.default] - Default handler if no specific handler matches
 * @returns {*} The result of the executed handler
 */
export const onPlatform = (handlers) => {
  const platform = getPlatform();

  // Check for specific platform handler first
  if (handlers[platform]) {
    return handlers[platform]();
  }

  // Check for native handler (covers both iOS and Android)
  if (isNative() && handlers.native) {
    return handlers.native();
  }

  // Fall back to default handler
  if (handlers.default) {
    return handlers.default();
  }

  return undefined;
};

/**
 * Convert a web URL to a native-compatible URL if needed
 * For native apps, ensures URLs work correctly with Capacitor
 * @param {string} url - The URL to convert
 * @returns {string}
 */
export const convertToNativeUrl = (url) => {
  if (!isNative()) {
    return url;
  }

  // Capacitor uses capacitor:// scheme on iOS
  // Relative URLs should work as-is since we're serving from the app
  return url;
};

export default {
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  isApplePlatform,
  getPlatform,
  isPluginAvailable,
  onPlatform,
  convertToNativeUrl
};
